// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — Pier3D.jsx   ·   THE BOARDWALK, IN THREE DIMENSIONS
// ────────────────────────────────────────────────────────────────────
// ⭐ THIS IS A RENDERER, NOT A GAME.
//
// It takes the SAME map, the SAME portals, the SAME interactables and
// the SAME position refs the 2D pier uses, and paints them in 3D.
// Game logic, Firebase, presence, party, chat, the Marketplace, the
// Crest Well and the entire HUD are untouched — they do not know this
// file exists. Swapping views is one boolean.
//
// ── ⭐ THE TOWN IS BUILT (2026-09-13) ──────────────────────────────
// caP'n: "no more floating rings and pictures — let's make it look like
// an actual real deal Pier." This supersedes the 2026-08-25 ruling that
// stood the 2D art up on camera-facing cards. Every wing is a building on
// the deck's edge whose door is the portal (pierBuild.jsx); the deck,
// rails, piles and lights are pierDeck.jsx; the landmarks are
// pierPieces.jsx and pierStalls.jsx. The PLAYERS are still the real 2D
// sprites — terror-assets sends no CORS headers, so a sprite can never
// become a texture, and billboard <Html> stays the only road for them.
//
// ⚠ drei's `<Html transform>` renders NOTHING on caP'n's machine.
// Proven twice, cost two sessions. Plain billboard <Html> is fine and is
// all this file uses. Do not "upgrade" it.
//
// ── THE CAMERA ──────────────────────────────────────────────────────
// THE CAMERA CONTRACT, all four pieces or none:
//   1 · two views, shared and PERSISTED — Follow (0.5×) and Trail (1×),
//       and a view is a distance MULTIPLIER, never a position.
//   2 · FREE POSE — drag and the rig stops steering and TRANSLATES the
//       pose you chose. It never re-aims, so a low shot stays a low
//       shot while you walk.
//   3 · DEFAULT is an ACTION, not a third view, and it is driven by a
//       COUNTER so pressing it while already on Trail still works.
//   4 · MOVEMENT FOLLOWS THE EYE. The rig publishes its ground-forward
//       to pierCamera.js and the ONE movement loop in UnstablePierre
//       rotates input by it. W means "away from the camera", always.
//
// ⚠ React context does NOT cross the <Canvas> boundary — the canvas
// runs its own reconciler. Everything shared with the outside goes
// through the pierCamera module, never a Provider.
//
// ── THE BACKDROP ────────────────────────────────────────────────────
// THE UNSTABLE WORLD is the standing fallback horizon, and the pier is
// explicitly a boardwalk over it. Reading chosen here: an ocean of
// CHAO — the well — because this is the pier where every currency in
// the Empire comes to be exchanged, and Chao is the well. Cracked
// eclipse rimmed in O'mnis, cracked by N'ullus. Zero new lights,
// fog={false} on everything out there, one useFrame per system,
// everything measured against the dome.
// ════════════════════════════════════════════════════════════════════

import React, {
  useRef, useMemo, useEffect, useState,
} from "react";
import { useBackdropActive, useBandReserve } from "./ieBackdrop";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import { getPlayerSprite, getIdleSprite, getCompanionSprite, PLAYER_SPRITES } from "./SpriteManifest";
import { isMobile } from "./PlayerEntity";
// ⚑ THE MODULE CYCLE IS GONE. This file used to import the 2D art back
// out of UnstablePierre (which imports this file) and stand it up on
// camera-facing cards. The pier is architecture now, and every structure
// lives in the build kit, which imports no screen.
import { mulberry32, canvasTex, textTex, useFontGen, releaseTextures } from "./pierTex";
import { Lampposts, CrestWell, HallOfDeeds, PierProps, PierInlays } from "./pierPieces";
import { BazaarStall, AllyStall, KeeperPost, WeatheredSign, PierGate, Monolith } from "./pierStalls";
import {
  VIEWS, VIEW_ORDER, getView, chooseView, viewMult, isPosed, setPosed,
  resetCamera, resetToken, subscribe, setGroundForward, resetGroundForward,
} from "./pierCamera";
// ⚑ THE PIER IS NOT EXEMPT FROM THE PIER'S OWN CHROME. This file used to
// import no theme at all and hand-paint #d4a03c, #f0d060, #c9a84c,
// #f6dfa0, #241f31, #8b8496, #59536a and #e8d5a3 — every one of which is
// a token that already existed. That is how a product ends up with
// thirteen golds. `pierCamera.js` imports nothing BY LAW; this is the
// renderer and has no such constraint.
import { UI, uiBtn, uiLabel } from "./theme";
import { BuildDriver } from "./pierBuild";
import { ShopSlot } from "./pierShopModels";
import { PierRecruits } from "./pierRecruits";
import { HeroBody, heroHeight, WillieFromTheForge, CompanionFollower, companionBeing } from "./pierFolk3D";   // Terror's Laostre / Lyraei / Forge Willie
import { FrameWatch } from "./sculptGate";                    // THE FAIL-SAFE LAW: sprites stand in if the pier can't keep up            // one being out front of every building   // a wing's Blender building, the code Shopfront as its fail-safe
import { SEA_Y } from "./pierDeck";
import { TURNED_AROUND } from "./pierPlan";
import { PierShell } from "./pierShell3D";
import { skyWorldProps, DEFAULT_SKY } from "./ieSkies";

// the worn sky, as the boardwalk wears it. Memoised per id so the horizon's
// props are stable objects (the dome painting is cached by canvasTex anyway).
const PIER_SKY_PROPS = new Map();
function pierSkyProps(skyId) {
  const id = skyId || DEFAULT_SKY;
  if (id === DEFAULT_SKY) return {};
  if (!PIER_SKY_PROPS.has(id)) {
    const p = skyWorldProps(id);
    PIER_SKY_PROPS.set(id, { reading: p.reading, tint: p.tint, rise: p.rise, hd: p.hd, domeTex: p.domeTex });
  }
  return PIER_SKY_PROPS.get(id);
}   // the Blender boardwalk; <Deck/> is its fail-safe

// ════════════════════════════════════════════════════════════════════
// DIALS — the only things to touch if this ever costs a frame.
// Never the geometry, and never the look.
// ════════════════════════════════════════════════════════════════════
const N_SHARDS   = 22;
const N_COLUMNS  = 9;
const N_MOTES    = 46;
const N_STARS    = 620;

// ⭐ THE ONE SCALE. How many CSS pixels of 2D art make one world tile,
// and the drei distanceFactor at which that is true.
//
// EVERYTHING in the town uses these two numbers and nothing else — no
// per-piece scale, no per-piece fudge. That is what makes the 3D pier
// the SAME TOWN as the 2D pier rather than a rebuilt one: a stall that
// is three tiles wide on the boardwalk is three tiles wide here,
// because both are reading the same art at the same tiles-per-pixel.
//
// ⚠ If the whole town looks too big or too small, DF_TILE is the only
// number to touch, and it moves everything together — which is the
// point. A per-piece scale is how a town stops being one town.
//
// ART_TILE is fixed rather than following the 2D view's responsive
// tile, so the world does not resize itself when the window does.
const ART_TILE = 48;
const DF_TILE  = 12;

// THE CANON COLOURS. Named so nobody has to remember which hex is which force.
const C = {
  nullus: "#B388FF", omnis: "#F2C14E", anima: "#FF4D8D", chao: "#7CE7FF",
  lyt: "#FFE9A0", lyf: "#7FE8D8", ruin: "#E8453C", shade: "#2A1540",
};

// ════════════════════════════════════════════════════════════════════
// ⚠ THE SPRITE PRELOAD — why the player sometimes wasn't there
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-25: "my character doesn't render sometimes."
//
// In the 2D pier the player's <img> is mounted once and its `src`
// changes as they turn. In here the whole node is remounted whenever
// the look changes, so a cold URL starts a fresh network fetch and the
// element is EMPTY until it lands — and an empty <img> with a size on
// it is exactly the little bordered box in the screenshot.
//
// Two fixes, both needed:
//   1. WARM EVERY SPRITE ONCE, at module load. The browser then has all
//      eight directions in cache before the player ever turns, so a
//      remount paints instantly instead of over the network.
//   2. NEVER RENDER A BROKEN BOX. On error, fall back to the gender's
//      default sprite; if that fails too, render nothing at all. A
//      missing character is a bug; a missing character wearing a broken
//      -image icon is a bug that looks like a broken game.
// ════════════════════════════════════════════════════════════════════
// true once real idle frames exist again (they 404 today — SpriteManifest)
const IDLE_ART = PLAYER_SPRITES.player_f_idle_a !== PLAYER_SPRITES.player_f_down_a;

let _warmed = false;
function warmSprites() {
  if (_warmed || typeof window === "undefined") return;
  _warmed = true;
  for (const url of Object.values(PLAYER_SPRITES)) {
    // no crossOrigin — terror-assets sends no CORS headers and tagging
    // it makes the request hard-fail. We never read these pixels.
    const im = new Image();
    im.src = url;
  }
}

function SpriteImg({ src, gender, size, glow }) {
  const [failed, setFailed] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const fallback = PLAYER_SPRITES[`player_${gender === "f" ? "f" : "m"}_default`];
  useEffect(() => { setFailed(false); setUseFallback(false); }, [src]);
  if (failed) return null;                       // silence beats a broken box
  return (
    <img
      src={useFallback ? fallback : src}
      alt="" draggable={false}
      onError={() => (useFallback ? setFailed(true) : setUseFallback(true))}
      style={{
        width: size, height: size, imageRendering: "pixelated",
        filter: glow || "none",
      }}
    />
  );
}

// ⚑ The PRNG, the texture cache, the painted word and the font generation
// live in pierTex.js — shared with the build kit, without a cycle.

// Scratch objects for the billboard maths — allocated once, never per
// frame. A `new THREE.Vector3()` inside useFrame is 60 allocations a
// second, per sign.
const _signQuat  = new THREE.Quaternion();

// A painted sign, floating and always facing you. Y-billboarded only —
// a sign that tips with the camera reads as a sticker on the floor.
export function PaintedSign({ text, color, position, height = 0.62, glow }) {
  const ref = useRef();
  const gen = useFontGen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tex = useMemo(() => textTex(text, color, { glow }), [text, color, glow, gen]);
  const w = height * (tex.userData.aspect || 4);
  useFrame(({ camera }) => {
    const o = ref.current; if (!o) return;
    // ⚠ WORLD POSITION, AND THE PARENT'S YAW TAKEN BACK OUT. `o.position`
    // is LOCAL, so a sign nested inside an offset group computed its yaw
    // from the wrong point — the holding's name over its own archway sits
    // inside a group offset by CX=12.5, which skewed it ~25° and swung it
    // the wrong way as you walked. Every caller that passed world coords
    // was fine, which is why this was latent.
    // ⚠ FULL BILLBOARD NOW (2026-09-13). Y-only was right for the portal
    // names that used to stand on the deck; the only PaintedSign left on the
    // pier is a player's NAMEPLATE, and under a high camera a Y-only plate
    // leaned ~20° whenever its player stood off to one side — a tilted card
    // floating over a head. It faces the lens exactly, parent rotation out.
    o.parent?.getWorldQuaternion(_signQuat);
    o.quaternion.copy(_signQuat.invert()).multiply(camera.quaternion);
  });
  return (
    <mesh ref={ref} position={position} renderOrder={4}>
      <planeGeometry args={[w, height]} />
      <meshBasicMaterial map={tex} transparent depthWrite={false} toneMapped={false} fog={false} />
    </mesh>
  );
}


// ════════════════════════════════════════════════════════════════════
// ⭐ THE SPEECH BUBBLE — what a player just said, over their head (2026-09-26)
// ════════════════════════════════════════════════════════════════════
// caP'n, watching Lyraei walk the pier: a line in the chat panel is easy
// to miss; a line over the head that said it is not. "let's add that now
// for all players." Every actor — you, every other player, an AI in the
// agent seat — shows their latest pier-chat line for a few seconds.
//
// ⚑ A PAINTED CANVAS, NOT <Html>. An <Html> is a DOM node rewriting its
//   transform every frame (ie-realtime-perf); a bubble is one plane and
//   one texture, made once per LINE, never per frame.
// ⚑ NO REACT STATE. The latest line per uid lives in `speechRef` (built by
//   UnstablePierre from the chat it already listens to — no new listener);
//   each bubble reads it in useFrame and swaps its own texture when the
//   line changes, so a chat message re-renders nothing in the scene.
// ⚑ LOCAL CLOCK. `at` is when THIS client first saw the line, so a sender
//   whose clock runs fast or slow can't make a bubble vanish or linger.
// ⚑ The old 2D floaties (emoji-only, positioned in 2D screen maths) are off
//   in 3D — this replaces them, and emoji-only lines bubble too.
// ⚠ 190 px per unit, not the nameplate's 320: at 330 a line read at about half the
// name's size and was unreadable from the trail camera (first live look, 2026-09-26).
const BUBBLE_PX = 190;                                   // canvas px per world unit
const BUBBLE_FONT = "600 40px 'Segoe UI', system-ui, -apple-system, sans-serif";
export const bubbleMs = (text) => Math.max(4000, Math.min(9000, 3200 + 70 * String(text || "").length));

// Works on CODE POINTS (Array.from), so a run of emoji is never cut through
// the middle of a surrogate pair. Clamped BEFORE any measuring: chat has no
// length cap, and a 5,000-character word measured one cut at a time froze
// every client in range for seconds (QA, 2026-09-26). Stops at maxLines.
export const BUBBLE_MAX_CHARS = 160;
function wrapBubble(ctx, text, maxW, maxLines = 3) {
  const cps = Array.from(String(text || "")).slice(0, BUBBLE_MAX_CHARS);
  const width = (arr) => ctx.measureText(arr.join("")).width;
  const words = cps.join("").split(/\s+/).filter(Boolean).map((w) => Array.from(w));
  const lines = [];
  let cur = [];
  // the longest prefix of `w` that fits, found by binary search (never < 1 code point)
  const fit = (w) => {
    let lo = 1, hi = w.length;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (width(w.slice(0, mid)) <= maxW) lo = mid; else hi = mid - 1; }
    return lo;
  };
  for (let w of words) {
    if (lines.length > maxLines) break;
    const test = cur.length ? [...cur, " ", ...w] : w;
    if (width(test) <= maxW) { cur = test; continue; }
    if (cur.length) { lines.push(cur); cur = []; }
    while (w.length && width(w) > maxW && lines.length <= maxLines) {
      const k = fit(w); lines.push(w.slice(0, k)); w = w.slice(k);
    }
    cur = w;
  }
  if (cur.length) lines.push(cur);
  const truncated = lines.length > maxLines;
  const out = lines.slice(0, maxLines);
  if (truncated) {
    let last = out[maxLines - 1];
    while (last.length > 1 && width([...last, "…"]) > maxW) last = last.slice(0, -1);
    out[maxLines - 1] = [...last, "…"];
  }
  return out.length ? out.map((l) => l.join("")) : [""];
}

let _probe = null;   // one measuring context for every bubble, made on first use

export function bubbleTex(text, edge = "#c9a84c") {
  if (!_probe) _probe = document.createElement("canvas").getContext("2d");
  const probe = _probe;
  probe.font = BUBBLE_FONT;
  const lines = wrapBubble(probe, text, 470);
  const lineH = 50, padX = 30, padY = 20, tail = 20, m = 4, r = 22;
  const tw = Math.max(...lines.map((l) => probe.measureText(l).width), 40);
  const w = Math.ceil(tw + padX * 2), h = Math.ceil(lines.length * lineH + padY * 2);
  const cv = document.createElement("canvas");
  cv.width = w + m * 2; cv.height = h + tail + m * 2;
  const ctx = cv.getContext("2d");
  const x0 = m, y0 = m, x1 = m + w, y1 = m + h, cx = cv.width / 2;
  ctx.beginPath();
  ctx.moveTo(x0 + r, y0);
  ctx.lineTo(x1 - r, y0); ctx.quadraticCurveTo(x1, y0, x1, y0 + r);
  ctx.lineTo(x1, y1 - r); ctx.quadraticCurveTo(x1, y1, x1 - r, y1);
  ctx.lineTo(cx + 16, y1); ctx.lineTo(cx, y1 + tail); ctx.lineTo(cx - 16, y1);   // the tail points at the speaker
  ctx.lineTo(x0 + r, y1); ctx.quadraticCurveTo(x0, y1, x0, y1 - r);
  ctx.lineTo(x0, y0 + r); ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
  ctx.closePath();
  ctx.fillStyle = "rgba(14, 9, 28, 0.9)";
  ctx.shadowColor = edge; ctx.shadowBlur = 14;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 3; ctx.strokeStyle = edge; ctx.stroke();
  ctx.font = BUBBLE_FONT;
  ctx.fillStyle = "#f4eeff";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  lines.forEach((l, i) => ctx.fillText(l, cx, y0 + padY + lineH * (i + 0.5)));
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.userData.w = cv.width / BUBBLE_PX;
  t.userData.h = cv.height / BUBBLE_PX;
  return t;
}

const _bubbleQuat = new THREE.Quaternion();
const _bubbleUp = new THREE.Vector3();
// `anchor` is the top of whatever sits over the head (the nameplate, or the head
// itself). The bubble stacks above it along the CAMERA's up, not world Y — both are
// billboards, so a world-Y gap shrank to nothing under a steep orbit and the
// bubble slid over the name (QA, 2026-09-26).
function SpeechBubble({ uid, speechRef, anchor, edge }) {
  const ref = useRef();
  const mat = useRef();
  const cur = useRef({ id: null, tex: null, until: 0, born: 0, h: 0 });
  // a bubble owns its texture: never cached, disposed when replaced or unmounted
  useEffect(() => () => { cur.current.tex?.dispose(); }, []);
  useFrame(({ camera }) => {
    const o = ref.current, m = mat.current;
    if (!o || !m) return;
    const c = cur.current, now = Date.now();
    const s = uid ? speechRef?.current?.[uid] : null;
    if (s && s.id !== c.id) {
      c.id = s.id;
      const life = bubbleMs(s.text);
      if (now - s.at < life) {
        c.tex?.dispose();
        c.tex = bubbleTex(s.text, edge);
        m.map = c.tex; m.needsUpdate = true;
        c.until = s.at + life; c.born = s.at; c.h = c.tex.userData.h;
        o.scale.set(c.tex.userData.w, c.tex.userData.h, 1);
      }
    }
    if (c.tex && now >= c.until) {
      // faded out: let the GPU copy go now, not at this player's next line
      c.tex.dispose(); c.tex = null; m.map = null; m.needsUpdate = true;
    }
    const live = !!c.tex;
    o.visible = live;
    if (!live) return;
    m.opacity = Math.max(0, Math.min(1, (now - c.born) / 180, (c.until - now) / 450));
    // the nameplate's full billboard, parent rotation taken back out
    o.parent?.getWorldQuaternion(_bubbleQuat);
    o.quaternion.copy(_bubbleQuat.invert()).multiply(camera.quaternion);
    // stack above the anchor along the bubble's own up (screen-up)
    _bubbleUp.set(0, 1, 0).applyQuaternion(o.quaternion);
    o.position.set(0, anchor, 0).addScaledVector(_bubbleUp, 0.08 + c.h / 2);
  });
  return (
    <mesh ref={ref} visible={false} renderOrder={6}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial ref={mat} transparent depthWrite={false} toneMapped={false} fog={false} />
    </mesh>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE UNSTABLE WORLD — the standing horizon
// ────────────────────────────────────────────────────────────────────
// Sized off the caller's bounds, so the same code fills a small room
// and this 56-tile boardwalk with no number changing.
//   dome = 3.4 · R   and every element measurably UNDER it.
// ════════════════════════════════════════════════════════════════════
// ⭐ EXPORTED 2026-09-14. The Minerium had forked its own horizon —
// four hand-built skies, each missing the shattered pocket dimensions
// and the Lyt/Lyf columns entirely, and with THE RISE turned into a
// ✹90,000 cosmetic when the doctrine says it is simply out there. A
// player walked off a boardwalk hanging over a living ocean of light,
// through a door, and stood under a poster with two circles on it.
// ⚑ One horizon, one file, both rooms. It costs nothing to share: every
// element is `meshBasicMaterial fog={false}` and adds ZERO lights.
// ⚠ `tint` AND `rise` ARE THE PURCHASABLE SKY'S DIFFERENTIATOR. The
// Minerium's four skies used to draw their own eclipse/holes/Rise; those
// were deleted when this shared horizon arrived, and without these two
// props a ✹90,000 Mythical sky and the free one would differ only by a
// gradient and a star count — the shelf's whole pitch, shipping nothing.
// ⚑ Both are optional, so the pier passes neither and is unchanged.
// ════════════════════════════════════════════════════════════════════
// ⭐⭐ A READING — every screen its own heaven (ie-unstable-world)
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-16: the skies must be "very unique from each other, not
// just slightly different colors." So a caller may pass a `reading`: which
// ocean, where the eclipse hangs and whether it is cracked yet, how many
// holes and of which colour, how crowded the shards and columns are, how
// heavy THE RISE is — and three extras a reading may add: a whole SUN low on
// the horizon, CLOUD streaks, and THE RISE's own PILLAR. Every field is
// optional; the pier passes no reading and is drawn exactly as before.
// Still zero lights, still fog={false}, still one useFrame.
// ════════════════════════════════════════════════════════════════════
// THE CRACK — glows hotter than the rim, branches once at midpoint. Seeded,
// so every eclipse in the Empire carries the same learnable wound.
function drawEclipseCrack(ctx, S, color) {
  const c = S / 2;
  const R2 = mulberry32(0x0EC1);
  const draw = (x0, y0, ang, len, w) => {
    ctx.beginPath(); ctx.moveTo(x0, y0);
    let x = x0, y = y0, a = ang;
    for (let i = 0; i < 8; i++) {
      a += (R2() - 0.5) * 0.7;
      x += Math.cos(a) * len / 8; y += Math.sin(a) * len / 8;
      ctx.lineTo(x, y);
    }
    ctx.lineWidth = w; ctx.strokeStyle = color;
    ctx.shadowColor = color; ctx.shadowBlur = S * 0.07; ctx.stroke();
    return { x, y, a };
  };
  const mid = draw(c - S * 0.20, c - S * 0.11, 0.55, S * 0.24, S * 0.014);
  draw(mid.x, mid.y, mid.a + 0.9, S * 0.16, S * 0.008);
  draw(mid.x, mid.y, mid.a - 0.5, S * 0.20, S * 0.010);
}

export const DEFAULT_READING = {
  ocean: "chao",
  eclipse: { show: true, x: -0.5, y: 0.45, z: -1, size: 1.5, rim: C.omnis, crack: C.nullus, cracked: true, pulse: false },
  holes: [{ x: 0.75, y: 0.55, z: -0.85, size: 1, hot: C.ruin, warm: "#ff9d6b" }],
  shards: 1, shardGlow: true, shardHot: [C.nullus, C.chao],
  columns: 1, columnCols: [C.lyt, C.lyf],
  motes: 1, moteCols: [C.omnis, C.chao, C.chao], moteSize: 1,   // i % 3 — the pier's own mix
  sun: null, clouds: null, pillar: null,
};
const OCEANS = {
  chao:  { deep: "#03141c", line: C.chao },
  anima: { deep: "#1c0410", line: C.anima },
  dusk:  { deep: "#1a0a10", line: "#ffb070" },
};

// ════════════════════════════════════════════════════════════════════
// ⭐⭐ THE REMASTERED SKY (caP'n, 2026-09-25) — "a ps1 game getting a ps3
// remaster". The same three painted pictures (dome, eclipse, Ruin Hole),
// rebuilt in Blender from the SAME SEEDS and rendered big:
// `_scratch/pier_sky/pier_sky_remaster.blend` → `public/skies/*.webp`.
// ⚑ `hd` is `{ dome, eclipse, hole }` from a sky's recipe in ieSkies.jsx. It only
// SWAPS MAPS — every mesh, placement, animation and the zero-lights law are
// untouched, and the canvas paintings stand in until the files arrive (or
// forever, if one fails to load). A missing image is a warning, never a void.
// ════════════════════════════════════════════════════════════════════
export function useHdSky(hd) {
  const [tex, setTex] = useState(null);
  const key = hd ? `${hd.dome}|${hd.eclipse}|${hd.hole}` : "";
  useEffect(() => {
    if (!hd) { setTex(null); return undefined; }
    let live = true;
    const loader = new THREE.TextureLoader();
    const base = process.env.PUBLIC_URL || "";
    const made = [];
    const load = (path) => new Promise((res) => {
      if (!path) { res(null); return; }
      loader.load(`${base}/${path}`, (t) => {
        t.colorSpace = THREE.SRGBColorSpace;   // same as canvasTex, or the remaster reads washed out
        t.anisotropy = 8;
        made.push(t); res(t);
      }, undefined, () => { console.warn("[pier] remastered sky image did not load — the painted one stands in:", path); res(null); });
    });
    Promise.all([load(hd.dome), load(hd.eclipse), load(hd.hole)]).then(([dome, eclipse, hole]) => {
      if (live) setTex({ dome, eclipse, hole });
      else made.forEach((t) => t.dispose());
    });
    return () => { live = false; made.forEach((t) => t.dispose()); setTex(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return tex;
}

export function UnstableWorld3D({ cx, cz, R, tint = null, rise = true, seaY = null, reading = null, hd = null, domeTex = null }) {
  const hdTex = useHdSky(hd);
  // ⚑ `seaY` lets a scene put the ocean where ITS piles meet it. Optional,
  // so the Minerium passes nothing and keeps its own depth.
  const SEA = seaY == null ? -R * 0.42 : seaY;
  const DOME = Math.max(300, 3.4 * R);
  const rdKey = reading ? JSON.stringify(reading) : "";
  const rd = useMemo(() => ({
    ...DEFAULT_READING, ...(reading || {}),
    eclipse: { ...DEFAULT_READING.eclipse, ...((reading && reading.eclipse) || {}) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rdKey]);
  const ecl = rd.eclipse;

  // ── THE SKY DOME ──────────────────────────────────────────────────
  const skyTex = useMemo(() => canvasTex("pier-sky", 1024, (ctx, S) => {
    const g = ctx.createLinearGradient(0, 0, 0, S);
    g.addColorStop(0.00, "#05030f");
    g.addColorStop(0.42, "#0b0620");
    g.addColorStop(0.72, "#150a2c");
    g.addColorStop(1.00, "#04030a");
    ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
    const R1 = mulberry32(0xA11E);
    // nebula bands — N'ullus violet and O'mnis gold, 5–14% alpha
    for (let i = 0; i < 7; i++) {
      const y = R1() * S, h = S * (0.05 + R1() * 0.16);
      const gg = ctx.createLinearGradient(0, y - h, 0, y + h);
      const col = i % 2 ? C.nullus : C.omnis;
      gg.addColorStop(0, col + "00");
      gg.addColorStop(0.5, col + Math.floor(13 + R1() * 22).toString(16).padStart(2, "0"));
      gg.addColorStop(1, col + "00");
      ctx.fillStyle = gg; ctx.fillRect(0, y - h, S, h * 2);
    }
    // hairline fractures — the same wound as the eclipse, running out
    ctx.lineWidth = 1;
    for (let i = 0; i < 14; i++) {
      ctx.strokeStyle = (i % 3 ? C.nullus : C.chao) + "22";
      ctx.beginPath();
      let x = R1() * S, y = R1() * S;
      ctx.moveTo(x, y);
      for (let k = 0; k < 7; k++) { x += (R1() - 0.5) * S * 0.16; y += (R1() - 0.5) * S * 0.11; ctx.lineTo(x, y); }
      ctx.stroke();
    }
    // stars, last and sparse
    // ⚠ THE SKY LOOKED EMPTY, and the reason is the mapping, not the
    // count. This square is wrapped EQUIRECTANGULARLY onto the dome, so
    // the top of the image is squeezed into the zenith — which is the
    // one part of the sky the camera never looks at (`maxPolarAngle`
    // keeps it near the horizon). Stars painted uniformly up there cost
    // texels and buy nothing. Spread across the band that is actually
    // on screen, and enough of them to read as a sky rather than as
    // dust. Two sizes and a bright minority, so it has depth.
    for (let i = 0; i < N_STARS; i++) {
      const a = 0.16 + R1() * 0.74;
      ctx.fillStyle = `rgba(240,238,255,${a})`;
      const sz = R1() < 0.88 ? 1 : 2;
      ctx.fillRect(R1() * S, S * (0.08 + R1() * 0.78), sz, sz);
    }
  }), []);

  // ── THE CRACKED ECLIPSE — the signature. Painted, never modelled. ──
  // a PULSING wound is drawn only on the pulse layer, or it can never dim (review)
  const painted = ecl.cracked && !ecl.pulse;
  const eclipseTex = useMemo(() => canvasTex(`pier-eclipse|${ecl.rim}|${ecl.crack}|${painted}`, 512, (ctx, S) => {
    const c = S / 2;
    const cor = ctx.createRadialGradient(c, c, S * 0.20, c, c, S * 0.5);
    cor.addColorStop(0, ecl.rim + "66");
    cor.addColorStop(0.42, ecl.rim + "1e");
    cor.addColorStop(1, "#00000000");
    ctx.fillStyle = cor; ctx.fillRect(0, 0, S, S);
    // the disc is a deep BRUISE, not black. Nothing here is honestly black.
    ctx.beginPath(); ctx.arc(c, c, S * 0.235, 0, 7); ctx.fillStyle = "#140A22"; ctx.fill();
    ctx.lineWidth = S * 0.012; ctx.strokeStyle = ecl.rim;
    ctx.shadowColor = ecl.rim; ctx.shadowBlur = S * 0.05; ctx.stroke();
    if (!painted) return;
    drawEclipseCrack(ctx, S, ecl.crack);
  }), [ecl.rim, ecl.crack, painted]);
  // the crack alone, for a reading whose wound PULSES (additive, over the disc)
  const crackTex = useMemo(() => (ecl.pulse ? canvasTex(`pier-crack|${ecl.crack}`, 512, (ctx, S) => {
    drawEclipseCrack(ctx, S, ecl.crack);
  }) : null), [ecl.pulse, ecl.crack]);

  // ── THE RUIN HOLE — a black hole that is RED. Two rates, or sticker. ──
  const holeTexOf = (hot, warm) => canvasTex(`pier-hole|${hot}|${warm}`, 512, (ctx, S) => {
    const c = S / 2;
    for (let i = 10; i >= 1; i--) {
      const r = S * 0.06 + (S * 0.42 * i) / 10;
      ctx.beginPath(); ctx.arc(c, c, r, 0, 7);
      ctx.strokeStyle = (i % 2 ? hot : warm) + Math.floor(8 + i * 7).toString(16).padStart(2, "0");
      ctx.lineWidth = S * 0.018; ctx.stroke();
    }
    ctx.beginPath(); ctx.arc(c, c, S * 0.115, 0, 7);
    ctx.strokeStyle = "#ffffffcc"; ctx.lineWidth = S * 0.012;
    ctx.shadowColor = "#fff"; ctx.shadowBlur = S * 0.05; ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.beginPath(); ctx.arc(c, c, S * 0.10, 0, 7); ctx.fillStyle = "#000"; ctx.fill();
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const holeTexs = useMemo(() => rd.holes.map((h) => holeTexOf(h.hot || C.ruin, h.warm || "#ff9d6b")), [rd]);

  // ── THE OCEAN OF CHAO — liquid light. Caustic FILAMENTS, not waves. ──
  const oc = OCEANS[rd.ocean] || OCEANS.chao;
  const oceanTex = useMemo(() => canvasTex(rd.ocean === "chao" ? "pier-ocean" : `pier-ocean|${rd.ocean}`, 512, (ctx, S) => {
    ctx.fillStyle = oc.deep; ctx.fillRect(0, 0, S, S);
    const R3 = mulberry32(0x0CEA);
    for (let i = 0; i < 150; i++) {
      ctx.beginPath();
      let x = R3() * S, y = R3() * S;
      ctx.moveTo(x, y);
      for (let k = 0; k < 6; k++) { x += (R3() - 0.5) * 70; y += (R3() - 0.5) * 34; ctx.lineTo(x, y); }
      ctx.strokeStyle = oc.line + Math.floor(10 + R3() * 40).toString(16).padStart(2, "0");
      ctx.lineWidth = 0.6 + R3() * 1.6;
      ctx.stroke();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rd.ocean]);
  const ocean = useRef();
  // Hoisted out of the JSX on purpose: a hook called from inside a
  // returned attribute is legal but reads as a landmine, and this one
  // also has a side effect (parking the texture on a ref for the frame
  // loop). Top of the body, where it can be seen.
  const oceanMap = useMemo(() => {
    const t = oceanTex.clone();
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(9, 9);
    t.needsUpdate = true;
    ocean.current = t;
    return t;
  }, [oceanTex]);
  // the clone is not in the texture cache, so it is freed here when the ocean
  // changes — captured, not read from the ref (which already holds the new one)
  useEffect(() => { const t = oceanMap; return () => t.dispose(); }, [oceanMap]);
  const shardsRef = useRef();
  const colsRef = useRef();
  const motesRef = useRef();
  const holesRef = useRef();
  const eclipse = useRef();
  const crackRef = useRef();
  const cloudsRef = useRef();
  const pillarRef = useRef();
  const sunRef = useRef();

  const bake = useMemo(() => {
    const R4 = mulberry32(0x5EED);
    const nS = Math.round(N_SHARDS * rd.shards), nC = Math.round(N_COLUMNS * rd.columns), nM = Math.round(N_MOTES * rd.motes);
    const shards = Array.from({ length: nS }, () => {
      const a = R4() * Math.PI * 2, d = R * (1.5 + R4() * 0.8);
      return {
        p: [cx + Math.cos(a) * d, -R * 0.10 + R4() * R * 0.9, cz + Math.sin(a) * d],
        s: R * (0.05 + R4() * 0.16),
        sq: [0.7 + R4() * 0.7, 0.5 + R4() * 0.8, 0.6 + R4() * 0.8],
        rot: [R4() * 6, R4() * 6, R4() * 6],
        spin: (R4() - 0.5) * 0.06,
        bob: 0.4 + R4() * 1.1,
        phase: R4() * 6,
        hot: R4() < 0.5 ? rd.shardHot[0] : rd.shardHot[1 % rd.shardHot.length],
      };
    });
    const columns = Array.from({ length: nC }, (_, i) => {
      const a = R4() * Math.PI * 2, d = R * (1.7 + R4() * 0.7);
      return {
        p: [cx + Math.cos(a) * d, 0, cz + Math.sin(a) * d],
        h: R * (0.9 + R4() * 1.5),
        w: R * (0.05 + R4() * 0.10),
        col: rd.columnCols[i % rd.columnCols.length],     // Lyt and Lyf are always a pair
        phase: R4() * 6,
        rate: 0.28 + R4() * 0.3,
      };
    });
    const motes = Array.from({ length: nM }, () => ({
      p: [cx + (R4() - 0.5) * R * 3, -R * 0.4 + R4() * R * 1.6, cz + (R4() - 0.5) * R * 2.4],
      rate: 0.5 + R4() * 1.5,
      phase: R4() * 6,
      s: R * (0.006 + R4() * 0.012) * rd.moteSize,
    }));
    // ⭐ the extras a reading may add
    const clouds = rd.clouds ? Array.from({ length: rd.clouds.count || 12 }, (_, i) => {
      const a = -Math.PI * 0.45 + R4() * Math.PI * 0.9, d = R * (1.9 + R4() * 0.6);   // the far half of the sky (z < cz)
      return { a, d, y: SEA + R * (0.45 + R4() * 0.9), w: R * (0.9 + R4() * 1.4), h: R * (0.05 + R4() * 0.08),
               col: rd.clouds.colors[i % rd.clouds.colors.length], drift: 0.004 + R4() * 0.006, op: 0.35 + R4() * 0.35 };
    }) : [];
    return { shards, columns, motes, clouds };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cx, cz, R, rd]);

  // THE SUN a reading may hang low on the horizon (Long Dusk): WHOLE, with
  // the eclipse only beginning to bite its edge — the hour before it cracks
  const sunTex = useMemo(() => (rd.sun ? canvasTex(`pier-sun-v2|${rd.sun.core}|${rd.sun.edge}`, 512, (ctx, S) => {
    const c = S / 2;
    const cor = ctx.createRadialGradient(c, c, S * 0.18, c, c, S * 0.5);
    cor.addColorStop(0, rd.sun.edge + "88"); cor.addColorStop(0.5, rd.sun.edge + "22"); cor.addColorStop(1, "#00000000");
    ctx.fillStyle = cor; ctx.fillRect(0, 0, S, S);
    const disc = ctx.createLinearGradient(0, c - S * 0.26, 0, c + S * 0.26);
    disc.addColorStop(0, rd.sun.core); disc.addColorStop(1, rd.sun.edge);
    ctx.beginPath(); ctx.arc(c, c, S * 0.26, 0, 7); ctx.fillStyle = disc; ctx.fill();
    // banded by the haze it is sinking through
    ctx.globalCompositeOperation = "destination-out";
    for (let k = 0; k < 6; k++) { const y = c + S * (0.04 + k * 0.038); ctx.fillRect(0, y, S, S * (0.006 + k * 0.003)); }
    ctx.globalCompositeOperation = "source-over";
    // the bruise arriving from the left
    // ⚠ clipped to the sun (visual check: unclipped it hung beside it as a dark disc)
    ctx.save();
    ctx.beginPath(); ctx.arc(c, c, S * 0.26, 0, 7); ctx.clip();
    ctx.beginPath(); ctx.arc(c - S * 0.36, c - S * 0.04, S * 0.24, 0, 7); ctx.fillStyle = "#1a0a22"; ctx.fill();
    ctx.restore();
  }) : null), [rd.sun]);
  const cloudTex = useMemo(() => (rd.clouds ? canvasTex("pier-cloud-streak", 256, (ctx, S, H) => {
    const g = ctx.createRadialGradient(S / 2, H / 2, 2, S / 2, H / 2, S / 2);
    g.addColorStop(0, "rgba(255,255,255,0.9)"); g.addColorStop(0.5, "rgba(255,255,255,0.35)"); g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, S, H);
  }, 64) : null), [rd.clouds]);
  const pillarTex = useMemo(() => (rd.pillar ? canvasTex(`pier-pillar|${rd.pillar.color}|${rd.pillar.core}`, 128, (ctx, W, H) => {
    const across = ctx.createLinearGradient(0, 0, W, 0);
    across.addColorStop(0, "rgba(0,0,0,0)"); across.addColorStop(0.35, rd.pillar.color + "88");
    across.addColorStop(0.5, rd.pillar.core); across.addColorStop(0.65, rd.pillar.color + "88"); across.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = across; ctx.fillRect(0, 0, W, H);
    // fading at the foot, burning at the top: it RISES
    ctx.globalCompositeOperation = "destination-in";
    const up = ctx.createLinearGradient(0, H, 0, 0);
    up.addColorStop(0, "rgba(0,0,0,0.15)"); up.addColorStop(0.6, "rgba(0,0,0,1)"); up.addColorStop(1, "rgba(0,0,0,0.4)");
    ctx.fillStyle = up; ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = "source-over";
  }, 512) : null), [rd.pillar]);

  // ⭐ ONE useFrame FOR THE WHOLE HORIZON. Not one per object.
  useFrame(({ clock, camera }) => {
    const t = clock.elapsedTime;
    // ⚠ ONE YAW, FROM POSITIONS — NOT FROM `camera.rotation.y`.
    // `Object3D.rotation` is an XYZ-order Euler, and every view in
    // `pierCamera.js` is yawed AND pitched down, so pitch bleeds into the
    // Y term and the decomposition is not the azimuth. Billboards driven
    // off it still swing edge-on at exactly the angles the billboard
    // exists to cure. `PaintedSign` in this same file has always done it
    // correctly; this is that line. The horizon is 30-45 units out, so
    // one yaw about the scene centre is close enough for all of it.
    // (the one scene-centre yaw that used to sit here is retired — see below)
    // ⭐⭐ EACH FLAT THING FACES THE CAMERA FROM WHERE IT IS (caP'n, 2026-09-25:
    // "the moon eclipse is probably angled wrong — we might need to fix all the
    // skies"). One yaw about the scene CENTRE was fine in the 24-tile yard and
    // wrong on the 56-tile pier: stand at one end and the centre is 30 units
    // away, so a sun or eclipse hung 2.2R out was turned toward a point that
    // is not you — seen edge-on it squashes into a tall sliver (Long Dusk's
    // sun read as a sword blade). Per object is one atan2 each, a dozen a
    // frame. ⚠ Valid because every one of these sits in an untransformed
    // parent (the horizon group is at the origin).
    const cpx = camera.position.x, cpz = camera.position.z;
    const face = (o) => { o.rotation.y = Math.atan2(cpx - o.position.x, cpz - o.position.z); };
    if (ocean.current) {
      ocean.current.offset.x = (t * 0.006) % 1;      // two axes, two rates,
      ocean.current.offset.y = (t * 0.0027) % 1;     // or it reads as a tile
    }
    const sh = shardsRef.current;
    if (sh) for (let i = 0; i < sh.children.length; i++) {
      const b = bake.shards[i], o = sh.children[i];
      o.rotation.y += b.spin * 0.016;
      o.position.y = b.p[1] + Math.sin(t * 0.35 + b.phase) * b.bob;
    }
    const cl = colsRef.current;
    if (cl) for (let i = 0; i < cl.children.length; i++) {
      const b = bake.columns[i], o = cl.children[i];
      // ⚠⚠ THE BILLBOARD THE COMMENT PROMISED AND THE CODE NEVER DID.
      // These are single planes scattered at random angles around the
      // dome, and nothing ever wrote a rotation to them — so most of the
      // Lyt/Lyf columns were edge-on to the camera and therefore
      // INVISIBLE. Same for the Ruin Hole group below, whose comment
      // says "billboard the GROUP" beside a group with no ref on it.
      // ⛑ Y-only, like `PaintedSign`: a column that tips with the
      // camera stops being a column standing on the sea.
      // Found by adversarial review 2026-09-14.
      face(o);
      // radiance SWELLS. It does not flicker — flicker is a fault, and
      // these are not faulty, they are enormous.
      const k = 0.5 + 0.5 * Math.sin(t * b.rate + b.phase);
      o.scale.x = 0.75 + k * 0.5;
      if (o.material) o.material.opacity = 0.20 + k * 0.26;
    }
    const mo = motesRef.current;
    if (mo) for (let i = 0; i < mo.children.length; i++) {
      const b = bake.motes[i], o = mo.children[i];
      // THE RISE. Everything in this Empire goes UP.
      const span = R * 2.0;
      o.position.y = b.p[1] + ((t * b.rate + b.phase * 3) % span);
      if (o.material) o.material.opacity = 0.25 + 0.35 * Math.sin(((t * b.rate + b.phase * 3) % span) / span * Math.PI);
    }
    const hs = holesRef.current;
    if (hs) for (let i = 0; i < hs.children.length; i++) {
      const gH = hs.children[i];
      face(gH);
      const halo = gH.children[0], disc = gH.children[1];   // no iterator per frame
      if (disc) disc.rotation.z = t * (0.05 + i * 0.012) * (i % 2 ? -1 : 1);
      if (halo) halo.rotation.z = -t * 0.021 * (i % 2 ? -1 : 1);      // counter-turn
    }
    if (crackRef.current) crackRef.current.material.opacity = 0.15 + 0.85 * (0.5 + 0.5 * Math.sin(t * 1.3));
    if (sunRef.current) face(sunRef.current);
    const cs = cloudsRef.current;
    if (cs) for (let i = 0; i < cs.children.length; i++) {
      const b = bake.clouds[i], o = cs.children[i];
      // drift, but swing back and forth within the far half — never round behind you
      const a = b.a + Math.sin(t * b.drift) * 0.35;
      o.position.set(cx + Math.sin(a) * b.d, b.y, cz - Math.cos(a) * b.d);
      face(o);
    }
    if (pillarRef.current) {
      face(pillarRef.current);
      const k = 0.5 + 0.5 * Math.sin(t * 0.45);     // it SWELLS
      pillarRef.current.scale.x = 0.8 + k * 0.45;
      pillarRef.current.material.opacity = 0.55 + k * 0.35;
    }
    if (eclipse.current) {
      const b = 1 + Math.sin(t * 0.5) * 0.018;     // it BREATHES; it does not spin
      eclipse.current.scale.set(b, b, 1);
      // ⚠ it faces you now on EVERY sky, the pier's own included — it used to
      // keep a fixed angle there, which is the "angled wrong" caP'n saw
      face(eclipse.current);
    }
  });

  const far = R * 2.2;    // MEASURED against DOME = 3.4R. Comfortably under.

  return (
    <group>
      {/* the dome. depthWrite off, so renderOrder is all that keeps it back */}
      <mesh renderOrder={-1} scale={[-1, 1, 1]} position={[cx, 0, cz]}>
        <sphereGeometry args={[DOME, 40, 24]} />
        <meshBasicMaterial map={hdTex?.dome || domeTex || skyTex} side={THREE.BackSide} fog={false} depthWrite={false} toneMapped={false} />
      </mesh>

      {/* THE OCEAN OF CHAO — the well. The pier hangs over it. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, SEA, cz]} fog={false}>
        <planeGeometry args={[R * 7, R * 7]} />
        <meshBasicMaterial map={oceanMap} fog={false} toneMapped={false} transparent opacity={0.92} />
      </mesh>

      {/* THE CRACKED ECLIPSE */}
      {/* ⚠ It used to sit at R*0.85 — about 19° up at this distance, and
          the rig aims DOWN at the player, so the eclipse clipped off the
          top of frame and read as a dark arch rather than as the sky's
          signature. R*0.45 is ~10°, which lands it in the picture. */}
      {/* THE RISE'S PILLAR — behind everything a reading hangs in front of it */}
      {rd.pillar && (
        // MEASURED: top at SEA + 2.8R, corner ≈ 3.3R, inside the 3.4R dome
        <mesh ref={pillarRef} position={[cx + far * ecl.x, SEA + R * 1.4, cz + far * ecl.z * 1.08]} fog={false} renderOrder={-1}>
          <planeGeometry args={[R * 0.7, R * 2.8]} />
          <meshBasicMaterial map={pillarTex} transparent depthWrite={false} fog={false} toneMapped={false} blending={THREE.AdditiveBlending} />
        </mesh>
      )}

      {/* A WHOLE SUN, low on the horizon — a reading may hang one (Long Dusk) */}
      {rd.sun && (
        <mesh ref={sunRef} position={[cx + far * (rd.sun.x ?? 0), SEA + R * (rd.sun.y ?? 0.2), cz - far * 1.02]} fog={false} renderOrder={0}>
          <planeGeometry args={[R * (rd.sun.size || 2.4), R * (rd.sun.size || 2.4)]} />
          <meshBasicMaterial map={sunTex} transparent depthWrite={false} fog={false} toneMapped={false} />
        </mesh>
      )}
      {rd.clouds && (
        <group ref={cloudsRef}>
          {bake.clouds.map((b, i) => (
            <mesh key={i} fog={false} renderOrder={0}>
              <planeGeometry args={[b.w, b.h]} />
              <meshBasicMaterial map={cloudTex} color={b.col} transparent opacity={b.op} depthWrite={false} fog={false} toneMapped={false} />
            </mesh>
          ))}
        </group>
      )}

      {ecl.show !== false && (
        <mesh ref={eclipse} position={[cx + far * ecl.x, R * ecl.y, cz + far * ecl.z]} fog={false} renderOrder={0}>
          <planeGeometry args={[R * ecl.size, R * ecl.size]} />
          <meshBasicMaterial map={(painted && hdTex?.eclipse) || eclipseTex} color={tint || "#ffffff"} transparent depthWrite={false} fog={false} toneMapped={false} />
          {crackTex && (
            <mesh ref={crackRef} position={[0, 0, 0.01]} fog={false} renderOrder={1}>
              <planeGeometry args={[R * ecl.size, R * ecl.size]} />
              <meshBasicMaterial map={crackTex} transparent depthWrite={false} fog={false} toneMapped={false} blending={THREE.AdditiveBlending} />
            </mesh>
          )}
        </mesh>
      )}

      {/* THE RUIN HOLES — billboard each GROUP, spin the discs INSIDE it */}
      <group ref={holesRef}>
        {rd.holes.map((h, i) => (
          <group key={i} position={[cx + far * h.x, R * h.y, cz + far * h.z]} scale={h.size || 1}>
            <mesh fog={false} renderOrder={0}>
              <planeGeometry args={[R * 1.5, R * 1.5]} />
              <meshBasicMaterial map={hdTex?.hole || holeTexs[i]} transparent opacity={0.22} depthWrite={false} fog={false} toneMapped={false} />
            </mesh>
            <mesh fog={false} renderOrder={1}>
              <planeGeometry args={[R * 0.85, R * 0.85]} />
              <meshBasicMaterial map={hdTex?.hole || holeTexs[i]} transparent depthWrite={false} fog={false} toneMapped={false} />
            </mesh>
          </group>
        ))}
      </group>

      {/* SHATTERED POCKET DIMENSIONS — faceted, and lit from INSIDE */}
      <group ref={shardsRef}>
        {bake.shards.map((b, i) => (
          <group key={i} position={b.p} rotation={b.rot} scale={b.sq}>
            {/* ⚠ FACETED AND LIT, NOT A FLAT DISC WITH A HALO. A flat dark
                shell (BasicMaterial shades every facet the same) inside a
                bright 1.5× BackSide shell drew each pocket dimension as a
                black blob wearing a pale RING — "floating rings", from the
                Minerium yard especially, where the sky fills the frame.
                Flat-shaded under the scene's existing lights, with the
                inner light on the emissive channel, the facets read as
                rock with something happening inside it. Still zero lights. */}
            <mesh fog={false}>
              <dodecahedronGeometry args={[b.s, 0]} />
              <meshStandardMaterial color="#2c2040" emissive={b.hot} emissiveIntensity={rd.shardGlow ? 0.28 : 0.02} flatShading roughness={0.55} metalness={0.25} fog={false} />
            </mesh>
            <mesh scale={1.22} fog={false} visible={rd.shardGlow}>
              <dodecahedronGeometry args={[b.s, 0]} />
              <meshBasicMaterial color={b.hot} side={THREE.BackSide} transparent opacity={0.07} depthWrite={false} fog={false} />
            </mesh>
          </group>
        ))}
      </group>

      {/* RADIATING LYT / LYF — always a pair. Y-billboarded by the rig. */}
      <group ref={colsRef}>
        {bake.columns.map((b, i) => (
          <mesh key={i} position={[b.p[0], SEA + b.h / 2, b.p[2]]} fog={false} renderOrder={0}>
            <planeGeometry args={[b.w * 2, b.h]} />
            <meshBasicMaterial color={b.col} transparent opacity={0.34} depthWrite={false} fog={false} toneMapped={false} />
          </mesh>
        ))}
      </group>

      {/* THE RISE. Always out there by doctrine — but a sky may sell a
          heavier one, so it is switchable rather than assumed. */}
      <group ref={motesRef} visible={rise !== false}>
        {bake.motes.map((b, i) => (
          <mesh key={i} position={b.p} fog={false}>
            <sphereGeometry args={[b.s, 5, 4]} />
            <meshBasicMaterial color={rd.moteCols[i % rd.moteCols.length]} transparent opacity={0.4} depthWrite={false} fog={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE ACTORS — the real 2D sprites, in the world
// ────────────────────────────────────────────────────────────────────
// Plain billboard <Html> with `center`. NEVER `transform` (renders
// nothing on caP'n's machine, proven twice). `distanceFactor` makes
// them shrink with perspective so they sit IN the scene rather than
// floating over it.
//
// The sprite direction is chosen relative to the CAMERA, not the
// world: walk away from the lens and you see their back, whichever way
// the camera happens to be standing. That is the whole reason a 2D
// sprite still reads correctly in a world you can orbit.
// ════════════════════════════════════════════════════════════════════
const camRelativeDir = (fx, fz, camYaw) => {
  const a = Math.atan2(fx, fz) - camYaw;      // facing, in camera space
  const q = Math.round(a / (Math.PI / 2)) & 3;
  // ⚠ q = 1 is facing +x (screen right, with the lens south of you), so it
  // is the RIGHT sprite. The old table had the side views swapped: every step
  // east showed the left-facing sprite (code review, confirmed live).
  return [{ x: 0, y: 1 }, { x: 1, y: 0 }, { x: 0, y: -1 }, { x: -1, y: 0 }][q];
};

function Actor({ read, gender, companionId, name, tint, isLocal, uid, speechRef }) {
  const g = useRef();
  useEffect(() => { warmSprites(); }, []);
  // ONE piece of state, and it only changes when the PICTURE changes.
  // ⚠ The walk _b frames are aliased to _a in SpriteManifest, so a
  // per-frame walk ticker would re-render this actor six times a second
  // to swap an image for itself. Only the female idle has a real second
  // frame, and it is slow. Do not add a walk ticker back until the _b
  // sprites actually exist.
  const [look, setLook] = useState({ dx: 0, dy: 1, moving: false, idleB: false });
  const lookRef = useRef(look);
  const lastP = useRef(read() || { x: 0, y: 0 });
  const acc = useRef(0);
  // ⚠ CAPTURED ONCE. r3f re-applies a `position` prop on every render,
  // so pointing it at a value the frame loop mutates would snap the
  // actor back to wherever the last look-change happened to leave it.
  // The frame loop owns the transform after mount; this is only where
  // it starts.
  const initPos = useRef([lastP.current.x, 0, lastP.current.y]);
  // ⭐ TERROR'S BODY (pierFolk3D.jsx): the control Terror's Plaza hands it —
  // world facing + walking or not. Mutated in the frame loop, never state.
  const ctl = useRef({ facing: { x: 0, y: 1 }, speed: 0, standIn: null }).current;

  useFrame(({ camera, clock }, dt) => {
    const o = g.current; if (!o) return;
    const p = read();
    if (!p) return;
    ctl.facing = { x: p.fx || 0, y: p.fy === undefined ? 1 : p.fy };

    // smooth toward the ref. Frame-rate independent, so a 144Hz monitor
    // and a 30fps laptop chase at the same real speed.
    const k = 1 - Math.pow(0.001, Math.min(dt || 0.016, 0.05));
    o.position.x += (p.x - o.position.x) * k;
    o.position.z += (p.y - o.position.z) * k;

    acc.current += dt || 0.016;
    if (acc.current < 0.14) return;            // re-evaluate at ~7Hz
    const moved = Math.hypot(p.x - lastP.current.x, p.y - lastP.current.y) > 0.004 * (acc.current / 0.016);
    ctl.speed = moved ? 1 : 0;
    acc.current = 0;
    lastP.current = { x: p.x, y: p.y };

    const camYaw = Math.atan2(camera.position.x - o.position.x, camera.position.z - o.position.z);
    const nd = camRelativeDir(p.fx || 0, p.fy || 1, camYaw);
    const idleB = Math.floor(clock.elapsedTime / 1.7) % 2 === 1;
    const cur = lookRef.current;
    if (nd.x !== cur.dx || nd.y !== cur.dy || moved !== cur.moving || (IDLE_ART && !moved && idleB !== cur.idleB)) {
      const next = { dx: nd.x, dy: nd.y, moving: moved, idleB };
      lookRef.current = next;
      setLook(next);
    }
  });

  // ⚠ While the idle art is missing from the CDN (SpriteManifest aliases it
  // to the walk-down frame), standing still keeps the way you were facing —
  // snapping to face the camera every time you stopped read as a glitch.
  const src = look.moving || !IDLE_ART
    ? getPlayerSprite(gender, look.dx, look.dy, false)
    : getIdleSprite(gender, look.idleB);
  const comp = companionId ? getCompanionSprite(companionId) : null;

  return (
    <group ref={g} position={initPos.current}>
      {/* the shadow that keeps them ON the planks rather than above them */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.42, 16]} />
        <meshBasicMaterial color="#000" transparent opacity={0.42} depthWrite={false} />
      </mesh>
      {/* ⚠ THE SAME SCALE AS THE TOWN AND AS THE 2D PIER. The player is
          1.5 tiles tall on the boardwalk (PlayerEntity: tile * 1.5) and
          the companion 1.3, so they are those sizes here, at the town's
          one distanceFactor. A character sized by a different number
          from the world they stand in is how a scene stops reading as
          one place. */}
      {/* ⭐ TERROR'S BODY — Laostre / Lyraei. The 2D sprite is its STAND-IN
          (loading, a failed glb, or a pier that can't keep up: sculptGate). */}
      {(ctl.standIn = () => (
        <Html center distanceFactor={DF_TILE} position={[0, 0.78, 0]}
              zIndexRange={[8, 0]} style={{ pointerEvents: "none" }}>
          <SpriteImg src={src} gender={gender} size={ART_TILE * 1.5}
                     glow={isLocal ? "drop-shadow(0 0 10px #c9a84c55)" : null} />
        </Html>
      )) && null}
      <HeroBody gender={gender} ctl={ctl} />
      {/* ⭐ THE SAME COMPANION AS TERROR, IN 3D (pierFolk3D CompanionFollower):
          a companion with a body walks at heel; one without keeps its picture */}
      {companionBeing(companionId) && <CompanionFollower id={companionId} owner={ctl} />}
      {comp && !companionBeing(companionId) && (
        // ⚠ at heel and smaller than its owner: at 1.3 tiles the companion was
        // the brightest, biggest thing on screen (visual review, 2026-09-14)
        <Html center distanceFactor={DF_TILE} position={[-0.62, 0.5, 0.38]}
              zIndexRange={[7, 0]} style={{ pointerEvents: "none" }}>
          <SpriteImg src={comp} gender={gender} size={ART_TILE} />
        </Html>
      )}
      {/* ⚠ 0.26 units of type floating 2.35 up, over a sprite that reads
          about one unit tall — a nameplate nearly three character-heights
          above the character's own head, at a third of their height.
          Closer and bigger: it belongs TO them. */}
      {name && <PaintedSign text={name} color={tint || "#e8d5a3"} position={[0, heroHeight(gender) + 0.28, 0]} height={0.4} />}
      {/* ⭐ what they just said, just above their name (THE SPEECH BUBBLE) */}
      <SpeechBubble uid={uid} speechRef={speechRef} edge={tint || "#e8d5a3"}
        anchor={heroHeight(gender) + (name ? 0.28 + 0.2 : 0.05)} />
    </group>
  );
}

// A remote player. ⚠ Their POSITION comes out of a ref inside useFrame,
// never out of a prop: presence ticks arrive several times a second per
// player, and letting those through React would reconcile the whole
// scene at exactly the rate everyone is walking.
function RemoteActor({ uid, meta, othersRef, speechRef }) {
  const read = useMemo(() => () => {
    const d = othersRef.current?.[uid];
    return d ? { x: d.x || 0, y: d.y || 0, fx: d.facingX || 0, fy: d.facingY ?? 1 } : null;
  }, [uid, othersRef]);
  return (
    <Actor
      read={read}
      gender={meta.gender} companionId={meta.companionId}
      name={meta.name} tint="#a78bfa"
      uid={uid} speechRef={speechRef}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE CAMERA RIG — THE CAMERA CONTRACT, all four pieces
// ════════════════════════════════════════════════════════════════════
const TMP_A = new THREE.Vector3();
const TMP_B = new THREE.Vector3();

function CameraRig({ posRef }) {
  const { camera, gl } = useThree();
  const controls = useRef();
  const lastTarget = useRef(new THREE.Vector3(posRef.current.x, 1, posRef.current.y));
  const lastReset = useRef(resetToken());

  // BASE framing — this scene's own idea of right. A view MULTIPLIES it.
  // ⚠ Raised 2026-09-13 when the pier grew buildings: from 5.6 up the
  // default shot looked straight across the south row of rooftops and the
  // boardwalk you stand on was the bottom third of nothing. The fader in
  // pierBuild handles what this does not.
  // ⚑ THE ASPECT FIT. A portrait phone sees a sliver of the street at the
  // desktop distance — a shopfront and a lamppost. Narrow screens stand the
  // lens further back (capped), so the boardwalk still reads as a place.
  // This is the scene's own framing; the view multiplier rides on top.
  const { size } = useThree();
  const fit = size.width < size.height ? Math.min(1.5, 0.78 * (size.height / size.width)) : 1;
  const BASE_DIST = 8.8 * fit, BASE_HEIGHT = 7.0 * fit;

  useEffect(() => () => resetGroundForward(), []);

  useFrame(() => {
    const c = controls.current; if (!c) return;
    const p = posRef.current;
    const tx = p.x, ty = 1.0, tz = p.y;

    // ── 4 · PUBLISH THE GROUND-FORWARD, every frame ────────────────
    camera.getWorldDirection(TMP_A);
    TMP_A.y = 0;
    if (TMP_A.lengthSq() > 1e-6) {
      TMP_A.normalize();
      setGroundForward(TMP_A.x, TMP_A.z);   // world z IS tile y
    }

    // ── 3 · DEFAULT is an ACTION, driven by a COUNTER ──────────────
    const tok = resetToken();
    if (tok !== lastReset.current) {
      lastReset.current = tok;
      const m = viewMult();
      camera.position.set(tx, ty + BASE_HEIGHT * m, tz + BASE_DIST * m);
      c.target.set(tx, ty, tz);
      lastTarget.current.set(tx, ty, tz);
      c.update();
      return;
    }

    if (isPosed()) {
      // ── 2 · FREE POSE — TRANSLATE the pose, never re-aim it ───────
      // Measure how far the look-target moved and move the camera by
      // the SAME delta. Re-aiming would snap a low eye-level shot back
      // to over-the-shoulder and the player would feel fought.
      TMP_B.set(tx - lastTarget.current.x, ty - lastTarget.current.y, tz - lastTarget.current.z);
      camera.position.add(TMP_B);
      c.target.set(tx, ty, tz);
      lastTarget.current.set(tx, ty, tz);
      c.update();
      return;
    }

    // ── 1 · the steered framing. GOAL-BASED, never command-based:
    // nothing sets the camera, things set a goal and every frame it
    // lerps a fraction of the way there. Buttery, and interruptible.
    const m = viewMult();
    TMP_B.set(tx, ty + BASE_HEIGHT * m, tz + BASE_DIST * m);
    camera.position.lerp(TMP_B, 0.055);
    c.target.lerp(TMP_A.set(tx, ty, tz), 0.12);
    lastTarget.current.copy(c.target);
    c.update();
  });

  return (
    <OrbitControls
      ref={controls}
      makeDefault
      enablePan={false}
      // the user ALWAYS wins — the goal dies the instant they grab it
      onStart={() => setPosed(true)}
      minDistance={2.4}
      maxDistance={34}
      // open enough that "down at eye level" is reachable, or the whole
      // free-pose feature is invisible
      maxPolarAngle={Math.PI / 1.9}
      enableDamping
      dampingFactor={0.08}
      domElement={gl.domElement}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// THE SCENE
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// ⭐⭐ THE ART SWAP POINT ⭐⭐
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-25: "eventually I will replace the 2D art with my own,
// so it has to be easy to replace." One row per landmark: its id in
// INTERACTABLES → the component that stands for it. Replace a row,
// replace a landmark. Wings are not in here — every wing is a Shopfront,
// placed by SHOPFRONTS in UnstablePierre.js.
// ⚠ "home" has no row on purpose: the Hall of Deeds stands on the plan's
// HALL_FRONT, and its interactable is only the doorstep.
// ════════════════════════════════════════════════════════════════════
const PIER_PIECES = {
  wily: KeeperWithWillie,          // W'ily's post, with Terror's Forge Willie behind it (caP'n: "he should be here")
  crest_well: CrestWell,
  old_sign: WeatheredSign,
  bazaar: BazaarStall,
  marketplace: AllyStall,
  mystery: Monolith,
};

// ⭐ W'ILY'S POST, MANNED. caP'n, pointing at the empty lectern and its "SPEAK · W'ily"
// prompt: "he should be here right I think". Willie stands behind the post, facing
// the walk; the post's own row (the `wily` INTERACTABLE, its dialogue) is unchanged.
function KeeperWithWillie({ n }) {
  return (
    <group>
      <KeeperPost n={n} />
      {/* ⚠ 0.3 left of the post's centre: dead behind it, its hanging lamp sat on his face */}
      <WillieFromTheForge n={{ x: n.x - 0.3, y: n.y - 0.85 }} />
    </group>
  );
}

function Scene({ world, posRef, facingRef, gender, companionId, playerName, roster, othersRef, skyId, playerUid, speechRef }) {
  const { cols, rows, portals, interactables, lampposts } = world;
  const cx = cols / 2, cz = rows / 2;
  const R = Math.max(cols, rows) / 2;
  // ⚠ Every painted name on the pier bakes the face loaded AT PAINT TIME.
  // When Cinzel lands, the town remounts ONCE so every sign repaints in it.
  const fontGen = useFontGen();

  // The local player's reader. Hoisted out of the JSX — a hook called
  // from inside a returned attribute is legal and reads as a landmine.
  const readLocal = useMemo(() => () => ({
    x: posRef.current.x, y: posRef.current.y,
    fx: facingRef.current.x, fy: facingRef.current.y,
  }), [posRef, facingRef]);

  // An interactable with no row in PIER_PIECES simply has no model — the
  // proximity pill still works, and the gap is obvious on the first walk past.
  const pieces = interactables.map((n) => {
    const Piece = PIER_PIECES[n.id];
    if (!Piece) return null;
    // ⭐ caP'n 2026-09-25: "turn bonnie and her wagon, the bazaar, and willie at his podium
    // around". Spun 180° about the piece's own spot; their collision circles are mirrored
    // to match in pierPlan.js (TURNED_AROUND). Remove the id from the set to undo.
    if (TURNED_AROUND.has(n.id)) {
      return (
        <group key={n.id} position={[n.x, 0, n.y]} rotation={[0, Math.PI, 0]}>
          <group position={[-n.x, 0, -n.y]}><Piece n={n} /></group>
        </group>
      );
    }
    return <Piece key={n.id} n={n} />;
  });

  return (
    <>
      {/* ⚑ FIXED LIGHT COUNT. THREE, forever — every lamp, window, neon and
          rift on the pier is emissive or basic material.
          The ambient became a HEMISPHERE (still one light): violet sky
          from above, warm timber bounce from below, so a wall facing away
          from the key reads as a shaded wall instead of a flat cut-out. */}
      {/* ⚠ Not too violet. At #8e86c4 every stretch of deck that no lamp
          or doorway washed read as lilac stone instead of timber at night. */}
      <hemisphereLight args={["#a89cb8", "#6a4a2c", 0.62]} />
      <directionalLight position={[18, 26, 12]} intensity={1.15} color="#ffdca4" />
      <directionalLight position={[-22, 14, -18]} intensity={0.42} color={C.nullus} />
      {/* depth: the far end of a 56-tile pier should sit back in the night.
          Everything past the deck is fog={false}, so the horizon is untouched. */}
      <fog attach="fog" args={["#150c28", 42, 150]} />

      {/* ⭐ ONE SKY, WORN EVERYWHERE (2026-09-25): the player's worn sky. The
          default passes nothing, so the pier is drawn exactly as it always was. */}
      <UnstableWorld3D cx={cx} cz={cz} R={R} seaY={SEA_Y} {...pierSkyProps(skyId)} />
      <PierShell world={world} />
      <group key={`town-${fontGen}`}>
        <PierGate world={world} />
        <Lampposts lamps={lampposts} />
        {portals.map((p) => (p.store ? <ShopSlot key={`shop-${p.id}`} portal={p} /> : null))}
        {world.hallFront && <HallOfDeeds front={world.hallFront} />}
        {pieces}
        <PierProps props={world.props || []} />
        {/* the recruit out front of every building (pierRecruits.jsx; who = pierPlan RECRUITS) */}
        <PierRecruits interactables={interactables} posRef={posRef}
          colorOf={(w) => (portals.find((p) => p.id === w) || {}).color || "#e8d9ff"} />
        <PierInlays world={world} />
      </group>
      <BuildDriver posRef={posRef} />

      <Actor
        read={readLocal}
        gender={gender} companionId={companionId}
        name={playerName} tint="#f0d060" isLocal
        uid={playerUid} speechRef={speechRef}
      />
      {roster.map((o) => (
        <RemoteActor key={o.uid} uid={o.uid} meta={o} othersRef={othersRef} speechRef={speechRef} />
      ))}

      <CameraRig posRef={posRef} />
      <FrameWatch />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE CAMERA BAR — ONE COMPONENT, EVERY ROOM
// ────────────────────────────────────────────────────────────────────
// ⚠ A CONTROL NOBODY CAN SEE IS A CONTROL THAT ISN'T THERE, and a
// control that changes shape when you walk through a door is one the
// player has to find again. The pier and the Minerium each carried their
// own copy of this bar, with a margin note in the yard's copy promising
// it "matches the pier's exactly" — beside a copy that was lime where
// the pier's was gold, radius 4 against 8, and declared in Courier.
// ⛑ The promise is structural now: there is one bar, and the Minerium
// imports it. A comment cannot drift from the code it describes if
// there is only one piece of code.
//
// ⚠ Also: it was the only thing on the pier in Courier New, with its own
// radius, its own border and four hand-rolled hexes from no palette — it
// read as a dev overlay somebody forgot to take out. `flexWrap` plus the
// right-hand gutter stop it sitting on top of the wordmark on a phone.
// ════════════════════════════════════════════════════════════════════
// * ⚠ This was the only thing on the pier in Courier New, with its
// own radius, its own border and four hand-rolled hexes that
// appear in no palette — it read as a dev overlay somebody forgot
// to take out. It is the house's chrome now, and `flexWrap` plus
// the right-hand gutter stop it sitting on top of the wordmark on
// a phone. *
export function CameraBar({ view, posed, force }) {
  // ⭐ OW-46: where the menu backdrop is up (the boardwalk) the camera views are
  // a row in it. Indoors it hides, so the Hall/Minerium keep this bar.
  const inBackdrop = useBackdropActive();
  if (inBackdrop) return null;
  return (
      <div style={{
        // ⚠ `left` + `right` + a non-auto `width` is over-constrained, so
        // `right` was simply ignored and did nothing the comment claimed.
        // What it has to clear is the D-pad: 154px wide at bottom-right,
        // z-200, and this bar is z-520 with pointer events ON — so on a
        // phone it sat over the DOWN arrow and ate the tap. Getting out
        // of the way is a WIDTH problem here, not a position one.
        position: "absolute", left: 12, bottom: 12, zIndex: 520,
        display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
        background: UI.panel, border: `1px solid ${UI.edge}`,
        padding: "6px 8px", borderRadius: UI.rMd, backdropFilter: "blur(6px)",
        fontFamily: UI.font, width: "fit-content",
        maxWidth: isMobile() ? "calc(100vw - 200px)" : "calc(100vw - 24px)",
      }}>
        <span style={{ ...uiLabel, paddingRight: 2 }}>CAMERA</span>
        {VIEW_ORDER.map((id) => (
          <button key={id} onClick={() => { chooseView(id); force((v) => v + 1); }}
            style={camBtn(view === id && !posed)}>{VIEWS[id].label}</button>
        ))}
        <button onClick={() => { resetCamera(); force((v) => v + 1); }} style={camBtn(false)}>DEFAULT</button>
        {posed && <span style={{ ...uiLabel, color: UI.gold }}>FREE</span>}
      </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE HOST
// ────────────────────────────────────────────────────────────────────
// ⚠ React.memo, and it is the single most important line in the file.
// UnstablePierre re-renders on every HUD tick; without this bail the
// whole 3D tree — boardwalk, horizon, every storefront and every actor
// — reconciles at that rate against a 60fps loop. The bail is SAFE
// because every live value reaches the scene through a REF the frame
// loop reads, which React neither sees nor needs to.
// ════════════════════════════════════════════════════════════════════
function Pier3DImpl({
  world, posRef, facingRef, gender, companionId, playerName,
  othersRef, rosterKey, skyId = null, playerUid = null, speechRef = null,
}) {
  // The town is the 2D art, and the 2D art animates off the Pier's
  // keyframe sheet. Injected here too, not only by the 2D view: a
  // child's effects run before its parent's, so without this the first
  // frame of every stall, bulb and sparkle would resolve to nothing.
  useEffect(() => { warmSprites(); }, []);
  // the cached textures let go of THIS renderer when the pier unmounts —
  // every Hall visit otherwise left the old renderer alive (see pierTex)
  useEffect(() => () => releaseTextures(), []);
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((v) => v + 1)), []);
  // ⭐ THE BAND'S ROOM: the canvas stops where the backdrop band begins
  // (Terror's RECORD_RESERVE, Sweet Dreams' viewW). Folded = full width.
  const bandReserve = useBandReserve();

  // Only nearby players become DOM. An <Html> is a real DOM node that
  // rewrites a transform every frame; twenty of them is a fine grain of
  // stutter nobody can point at.
  //
  // ⚠ THE ROSTER, NOT THE POSITIONS. This recomputes only when
  // `rosterKey` changes — that is, when somebody arrives or leaves.
  // Where everyone IS lives in `othersRef` and is read in useFrame.
  // ⚠ NO DISTANCE FILTER. This list is only rebuilt when someone arrives or
  // leaves, so a player who was far away AT THAT MOMENT was dropped for good —
  // walk over to a friend at the Marketplace and nobody was there, while the
  // proximity pill still offered to party up with them. Presence already caps
  // the others at four, which is the whole DOM budget this needs.
  // (Third adversarial review, CONFIRMED.)
  const roster = useMemo(() => {
    return Object.entries(othersRef.current || {})
      .filter(([, d]) => !!d)
      .slice(0, 8)
      .map(([uid, d]) => ({ uid, gender: d.gender, companionId: d.companionId, name: d.name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterKey, othersRef, posRef]);

  const view = getView();
  const posed = isPosed();

  return (
    <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, right: bandReserve, background: "#04030a" }}>
      <Canvas
        dpr={[1, 1.5]}
        shadows={false}
        // ⚠ near 0.25 / far 1600, not 0.1 / 4000: depth precision is set by
        // the RATIO, and a town of thin boards a centimetre apart z-fights
        // at walking distance on the old one. The dome is 300 out; 1600 is
        // still five times what the horizon needs.
        camera={{ fov: 55, near: 0.25, far: 1600, position: [world.spawn.x, 7, world.spawn.y + 9] }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        onCreated={({ gl }) => {
          gl.setClearColor("#04030a");
          // dev only: lets the preview read draw calls (`__pierGL.info.render.calls`)
          if (process.env.NODE_ENV === "development") window.__pierGL = gl;
        }}
      >
        <Scene
          world={world}
          posRef={posRef}
          facingRef={facingRef}
          gender={gender}
          companionId={companionId}
          playerName={playerName}
          roster={roster}
          othersRef={othersRef}
          skyId={skyId}
          playerUid={playerUid}
          speechRef={speechRef}
        />
      </Canvas>

      {/* ── THE CAMERA BAR ────────────────────────────────────────────
          ⚠ A CONTROL NOBODY CAN SEE IS A CONTROL THAT ISN'T THERE.
          This screen renders inside another screen, and that host
          stacks its own overlays high — so the host's z-stack is part
          of this component's contract. Same corner, every room. */}
      <CameraBar view={view} posed={posed} force={force} />
    </div>
  );
}

// ⚑ The same button the rest of the Empire uses, so a control does not
// change species when you walk through a door.
const camBtn = (on) => ({ ...uiBtn(UI.gold, { on }), fontSize: 9.5, padding: "5px 10px" });

export default React.memo(Pier3DImpl);
